import { type BatchItem, useBatches } from '@api/hooks/useBatch';
import { Skeleton } from '@components/skeleton/Skeleton';
import {
	CardItem,
	ChevronLeftIcon,
	DocumentIcon,
	Typography,
} from '@gurupay/gp-ui-library';
import { formatNumberAmount } from '@utils/utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BatchStatus } from 'types/operations';

export const BatchTransactions = () => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useBatches({
		limit: 3,
		currentPage: 1,
		status: [BatchStatus.EXECUTED, BatchStatus.EXECUTED_WITH_WARNINGS],
	});
	const transactions: BatchItem[] = data?.items ?? [];

	if (!isLoading && (isError || !transactions.length)) {
		return null;
	}

	return (
		<div className="space-y-4">
			<Typography className="flex flex-row gap-space-12" level="h3">
				{t('paymentsPage.batchPayments')}

				{!isError && !!transactions.length && !isLoading && (
					<Link to="/operations/batch">
						<ChevronLeftIcon className="rotate-180 text-brand-secondary-700" />
					</Link>
				)}
			</Typography>

			{isLoading ? (
				<div className="flex flex-row overflow-x-auto lg:overflow-visible gap-space-16">
					<Skeleton className="h-[100px] w-[calc(100%/3-11px)] min-w-[250px]" />
					<Skeleton className="h-[100px] w-[calc(100%/3-11px)] min-w-[250px]" />
					<Skeleton className="h-[100px] w-[calc(100%/3-11px)] min-w-[250px]" />
				</div>
			) : (
				<ul className="flex flex-row overflow-x-auto lg:overflow-visible gap-space-16">
					{transactions.map((transaction) => (
						<CardItem
							key={transaction.uuid}
							icon={<DocumentIcon />}
							linkClassName="border border-neutral-100 rounded-md px-space-16 w-[calc(100%/3-11px)] min-w-[250px]"
							subTitle={`${formatNumberAmount(transaction.totalAmount.toFixed(2))} ${transaction?.currency}`}
							title={transaction.fileName}
							to={`/operations/batch/${transaction.uuid}`}
						>
							{transaction.executedAt || transaction.uploadDate}
						</CardItem>
					))}
				</ul>
			)}
		</div>
	);
};
